import React, { useRef } from "react";
import PropTypes from "prop-types";

import EditorCloseIcon from "@atlaskit/icon/glyph/chevron-right";
import styled from "styled-components";

import SimpleBar from "../Simplebar";
import { useClickOutside } from "../../Hooks/useClickOutside";
import { SimpleTabs, Tab } from "../Tabs";
import { useTheme } from "styled-components";

const SidebarContainer = styled.div`
  display: flex;
  position: absolute;
  overflow: hidden;
  top: 0;
  bottom: 0;
  justify-content: ${({ position }) => (position === "left" ? "end" : "start")};
  width: ${({ width, isOpen }) => (isOpen ? width : 0)}px;
  ${({ position }) => position}: 0;
  background: ${({ theme }) => theme.bg.general};
  z-index: 10;
  border-left: 1px solid ${({ theme }) => theme.border.black10};
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.12);
  transition: visibility 0.1s, width 0.2s;
`;

const Sidebar = styled.div`
  box-sizing: border-box;
  height: 100%;
  padding: 20px;
  min-width: ${({ width }) => width}px;
`;

const ScrollbarWrapper = styled.div`
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 400;

  ${({ showTabs }) => {
    if (showTabs) {
      return `border-bottom: 2px solid ${({ theme }) => theme.border.black15}`;
    }

    return "";
  }}
`;

const CloseIcon = styled.div`
  cursor: pointer;
  border-radius: 50%;
  position: fixed;
  height: 23px;
  width: 23px;
  margin-left: -32px;
  background: ${({ color }) => color};
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.3);

  &:hover {
    filter: brightness(0.97);
  }
`;

const SidebarTransition = React.memo(
  ({
    isOpen,
    position,
    close,
    width,
    additionalStyle,
    containerStyles,
    className,
    showTabs,
    showHeader,
    tabs,
    selectedTab,
    setSelectedTab,
    shouldHideScrollbar,
    children,
    title,
  }) => {
    const dialogRef = useRef();
    const theme = useTheme();

    useClickOutside(dialogRef, close);

    return (
      <SidebarContainer
        ref={dialogRef}
        isOpen={isOpen}
        width={width}
        position={position}
        className={className}
        style={{ ...containerStyles }}
      >
        <Sidebar width={width}>
          {showHeader && (
            <Header hasBorder={showTabs}>
              {showTabs ? (
                <>
                  {!!tabs.length && (
                    <SimpleTabs>
                      {tabs.map((tab) => (
                        <Tab
                          key={tab}
                          color={theme.shared.sidebar.tabs}
                          isSelected={selectedTab === tab}
                          onClick={() => setSelectedTab(tab)}
                        >
                          {tab}
                        </Tab>
                      ))}
                    </SimpleTabs>
                  )}
                </>
              ) : (
                <span style={{ color: theme.shared.tabs.active }}>{title}</span>
              )}

              {isOpen && position === "right" && (
                <CloseIcon
                  onClick={() => close()}
                  color={theme.shared.sidebar.imagePicker.input}
                >
                  <EditorCloseIcon primaryColor={theme.shared.sidebar.tabs} />
                </CloseIcon>
              )}
            </Header>
          )}

          <ScrollbarWrapper>
            {shouldHideScrollbar ? (
              <>{children}</>
            ) : (
              <SimpleBar
                style={{
                  ...additionalStyle,
                  overflowX: "hidden",
                  height: "calc(100% - 40px)",
                }}
              >
                {children}
              </SimpleBar>
            )}
          </ScrollbarWrapper>
        </Sidebar>
      </SidebarContainer>
    );
  }
);

SidebarTransition.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  position: PropTypes.oneOf(["left", "right"]),
  close: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  width: PropTypes.number,
  additionalStyle: PropTypes.objectOf(PropTypes.string),
  containerStyles: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  showHeader: PropTypes.bool,
  showTabs: PropTypes.bool,
  title: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.string),
  selectedTab: PropTypes.string,
  setSelectedTab: PropTypes.func,
  shouldHideScrollbar: PropTypes.bool,
};

SidebarTransition.defaultProps = {
  children: undefined,
  className: "",
  position: "right",
  title: "",
  width: 400,
  additionalStyle: {},
  containerStyles: {},
  showHeader: true,
  showTabs: false,
  tabs: [],
  selectedTab: "",
  setSelectedTab: () => {},
  shouldHideScrollbar: false,
};

SidebarTransition.displayName = "SidebarTransition";

export default SidebarTransition;
